import { StaticRole } from './user-roles.model';
import { PageAttributes, SortAttributes } from '@markant-platform/shared/table/model';
import { ErrorResponse } from '@markant-platform/shared/error-model';
import { Address, OrganizationDetailData, OrganizationMembership, SalutationType, UserType } from 'data-model';

export enum DepartmentAffiliations {
  MANAGEMENT = 'MANAGEMENT',
  HUMAN_RESOURCES_DEPARTMENT = 'HUMAN_RESOURCES_DEPARTMENT',
  BOOKKEEPING_OR_ACCOUNTING = 'BOOKKEEPING_OR_ACCOUNTING',
  CONTROLLING = 'CONTROLLING',
  PURCHASING_OR_PROCUREMENT = 'PURCHASING_OR_PROCUREMENT',
  FINANCES = 'FINANCES',
  LEGAL_DEPARTMENT = 'LEGAL_DEPARTMENT',
  RESEARCH_AND_DEVELOPMENT = 'RESEARCH_AND_DEVELOPMENT',
  MANUFACTURING_OR_PRODUCTION = 'MANUFACTURING_OR_PRODUCTION',
  QUALITY_MANAGEMENT = 'QUALITY_MANAGEMENT',
  PUBLIC_RELATIONS = 'PUBLIC_RELATIONS',
  MARKETING = 'MARKETING',
  IT = 'IT',
  DISTRIBUTION = 'DISTRIBUTION',
  LOGISTICS = 'LOGISTICS',
  INVENTORY_MANAGEMENT = 'INVENTORY_MANAGEMENT',
  CUSTOMER_CARE = 'CUSTOMER_CARE',
  FACILITY_MANAGEMENT = 'FACILITY_MANAGEMENT',
  BUSINESS_DEVELOPMENT = 'BUSINESS_DEVELOPMENT',
  CATEGORY_MANAGEMENT = 'CATEGORY_MANAGEMENT',
  MARKET_RESEARCH = 'MARKET_RESEARCH',
  EXPORT = 'EXPORT',
  REVISION = 'REVISION',
  AZUBI = 'AZUBI',
  COMPLIANCE = 'COMPLIANCE',
  CONSULTING = 'CONSULTING',
  PIM = 'PIM',
  PRODUCTMANAGEMENT = 'PRODUCTMANAGEMENT',
  PROJECTMANAGEMENT = 'PROJECTMANAGEMENT',
}

export interface User {
  id?: string;
  address?: Address;
  salutation?: SalutationType;
  firstname: string;
  lastname?: string;
  email?: string;
  language?: string;
  userType: UserType;
  mfa?: boolean;
  phone?: string;
  position?: string;
  organization?: OrganizationDetailData;
  organizationMemberships?: OrganizationMembership[];
  mfaSettings?: {
    mfaMandatory: boolean;
    version: number;
  };
  version?: number;
  skipMfa?: boolean;
}

export type PlatformRequestUser = {
  id?: string;
  salutation?: SalutationType;
  firstname: string;
  lastname?: string;
  email?: string;
  organizationMembershipStatus: UserOrganizationStatus;
};

export type PlatformRequestUserFilterParameters = {
  search?: string;
  page?: PageAttributes;
  sort?: SortAttributes;
};

export type MembershipOriginInformation = {
  createdByFullName: string | null;
  createdByIsActiveInOrganization: boolean | null;
  becameActiveAt: Date | null;
  createdAt: Date;
  originatedFrom: 'REGISTRATION' | 'INVITATION' | null;
};

export interface Auth0Enrollment {
  id: string;
  type: string;
  confirmed: string;
  name: string;
}

export interface UserDTO {
  id: string;
  salutation: SalutationType;
  firstname: string;
  lastname: string;
  email: string;
  pendingEmailUpdate?: string;
  phone?: string;
  language?: string;
  userType: UserType;
  platformRoles?: StaticRole[];
  organizationMemberships?: OrganizationMembershipDto[];
  version: number;
  externalId?: string;
  ssoEnabled: boolean;
  verified?: boolean;
  activeOrganizationMemberships?: boolean;
  invitedOrganizationMemberships?: boolean;
}

export interface OrganizationMembershipDto {
  becameActiveAt?: Date | null;
  createdAt?: Date;
  organizationId: string;
  organizationName: string;
  userOrganizationStatus: UserOrganizationStatus;
  position?: string;
  phone?: string;
  address?: Address;
  department?: string;
  roles: StaticRole[];
}

export interface UserInviteToOrganizationDto {
  salutation: SalutationType;
  firstname: string;
  lastname: string;
  email: string;
  organizationRole: string;
  organizationId: string;
  language?: string;
  position: string;
  department: string;
  groupIds?: string[];
  cartridgeRoles: CartridgeRole[];
}

export interface CartridgeRole {
  roleId: string;
  cartridgeVariantId: string;
  suborganizationIds?: string[];
}

export interface DeactivateOrganizationMembershipsRequestDto {
  userId: string;
  version: number;
}

export interface DeactivateOrganizationMembershipsResponseDto {
  deactivatedUserIds: string[];
  failures: Map<string, ErrorResponse>;
  withFailure: boolean;
}

export interface OrganizationUserDTO
  extends Omit<UserDTO, 'organizationMemberships' | 'externalId'>,
    OrganizationMembershipDto {
  cartridgeRights: string[];
}

export interface UserUpdateDTO {
  id: string;
  salutation: string;
  firstname: string;
  lastname: string;
  email: string;
  language: string;
  phone?: string;
  position?: string;
  department?: string;
  address?: Address;
  organizationId?: string;
  version?: number;
  reason?: string;

  [index: string]: unknown;
}

export interface UpdateUserLanguageRequestDto {
  language: string;

  [index: string]: unknown;
}

export interface UpdateUserLanguageResponseDto {
  language: string;

  [index: string]: unknown;
}

export interface CartridgeUserDTO {
  id: string;
  salutation?: 'm' | 'w' | 'd';
  firstname: string;
  lastname: string;
  userType: UserType;
  email: string;
  roles: string[];
  organizationName: string;
  organizationId?: string;
}

export interface UserIdDTO {
  userId: string;
}

export interface UserNameDto {
  id: string;
  firstname: string;
  lastname: string;
  email: string;
}

export interface UserFullNameDto {
  id: string;
  fullName: string;
  lastName: string;
}

export interface AlternativeCartridgeVariantOwnersResponseDto {
  owners: CartridgeVariantCurrentOwnersResponseDto[];
  alternativeOwners: CartridgeVariantAlternativeOwnerResponseDto[];
}

export interface CartridgeVariantCurrentOwnersResponseDto {
  userId: string;
  salutation: string;
  firstname: string;
  lastname: string;
  ownedCartridgeVariants: OwnedCartridgeVariantResponseDto[];
}

export interface OwnedCartridgeVariantResponseDto {
  id: string;
  name: string;
}

export interface CartridgeVariantAlternativeOwnerResponseDto {
  userId: string;
  salutation: string;
  firstname: string;
  lastname: string;
}

export enum UserOrganizationStatus {
  ACTIVE = 'ACTIVE',
  INVITED = 'INVITED',
  DELETED = 'DELETED',
  BLOCKED = 'BLOCKED',
}

export const UserOrganizationStatusMap: Record<string, { color: string; icon: string }> = {
  ACTIVE: { color: 'success', icon: 'm_checkmark-small' },
  INVITED: { color: 'warning', icon: 'm_sent-small' },
  DELETED: { color: 'error', icon: 'm_trash' },
  BLOCKED: { color: 'dark', icon: 'm_close-small' },
};

export const UserRolesMap: Record<string, { color: string; icon: string }> = {
  ORGANIZATION_ADMIN: { color: 'secondary', icon: '' },
  ORGANIZATION_MEMBER: { color: 'secondary', icon: '' },
  LEISTUNG_ADMIN: { color: 'secondary', icon: '' },
  PLATFORM_ADMIN: { color: 'secondary', icon: '' },
  DEMO_USER: { color: 'secondary', icon: '' },
};

export const UserTypeMap: Record<string, { color: string; icon: string }> = {
  TECHNICAL: { color: 'secondary', icon: '' },
};

export interface UserNamesDTO {
  userNames: Record<string, { firstName: string; lastName: string }>;
}

export interface UserGuidedTourDTO {
  completedSections: number[];
}
